import React, { useContext } from "react";
import { T } from "../../hooks/useTranslate";

import { useLocation, useNavigate } from "react-router-dom";

import { Stack, Typography, Divider, Box } from "@mui/material";

import { Avatar } from "@unicity/design-system";
import { LanguageContext } from "../../hooks/LanguageContext";

import { formatDateByLocale } from "../../common/date-functions"
import { DateTime } from "luxon";
import { useMixPanel } from "hooks/useMixPanel";
import { useApi } from "../../hooks/useApi";

const toDisplayEvents = [
  "new-auto-order",
  "created-order-for-existing-customer",
  "created-order-with-transactional-customer",
  "upgraded-customer-type-from-member-to-associate"
]

const nonOrderEvents = [
  "upgraded-customer-type-from-member-to-associate"
]

// GO-3516: the order copy follows activityType, not the Hydra event name. A Partial
// Member's first order arrives as activityType "first-order" with hydraEventType
// "created-order-for-existing-customer" (GO-3515). Items without an activityType
// fall back to the event name.
const isFirstOrderActivity = ({ activityType, hydraEventType }) =>
  activityType
    ? activityType === "first-order"
    : hydraEventType === "created-order-with-transactional-customer";

const RenderName = ({ firstName, lastName, isClickable }) => {
  return (
    <Typography
      data-test-id="activity-feed-item-name"
      variant="widgetItemName"
      sx={{ fontWeight: "bold", cursor: isClickable ? "pointer" : "default" }}
    >
      {firstName}{lastName ? ` ${lastName}` : ""}
    </Typography>
  )
}

const RenderEventDetails = ({
  activityType,
  hydraEventType,
  orderDetails,
  localizeNumber,
  isClickable,
  isAffiliate,
  currentLanguage
}) => {
  const orderValue = isAffiliate ? orderDetails?.subtotal : orderDetails?.pv;
  const orderValueLabel = isAffiliate ? "subtotal" : "pv";
  const hasOrderValue = orderDetails && orderValue != null;
  const formatOrderValue = (value) => isAffiliate
    ? new Intl.NumberFormat(currentLanguage, { style: "currency", currency: orderDetails?.currency || "USD" }).format(value)
    : localizeNumber(value);

  switch (hydraEventType) {
    case "new-auto-order":
      return (
        <>
          &nbsp;<T>started_a</T>&nbsp;
          {orderDetails
            ? (
              <>
                <Typography
                  data-test-id={`activity-feed-item-pv-value`}
                  variant="widgetItemName"
                  sx={{ fontWeight: "bold", cursor: isClickable ? "pointer" : "default" }}>
                  {hasOrderValue ? <>{formatOrderValue(orderValue)}{!isAffiliate && <>&nbsp;<T>{orderValueLabel}</T></>}</> : null}
                </Typography>&nbsp;
                <T>subscription_low</T>.
              </>
            )
            : (<><T>subscription_low</T>.</>)
          }
        </>
      )
    case "created-order-for-existing-customer":
    case "created-order-with-transactional-customer": {
      const isFirst = isFirstOrderActivity({ activityType, hydraEventType });
      const regularLabel = orderDetails ? "made_a" : "made_an";
      const madeLabel = isFirst ? "made_their_first" : regularLabel;
      return (
        <>
          &nbsp;<T>{madeLabel}</T>&nbsp;
          {
            hasOrderValue &&
            <>
              <Typography
                data-test-id={`activity-feed-item-pv-value`}
                variant="span"
                sx={{ fontWeight: "bold", cursor: isClickable ? "pointer" : "default" }}>
                {formatOrderValue(orderValue)}{!isAffiliate && <>&nbsp;<T>{orderValueLabel}</T></>}
              </Typography>&nbsp;
            </>
          }
          <T>order</T>.
        </>
      )
    }
    case "upgraded-customer-type-from-member-to-associate":
      return (
        <>
          &nbsp;
          <T>is_now_a</T>
          &nbsp;
          <Typography variant="span" sx={{ fontWeight: "bold", cursor: isClickable ? "pointer" : "default" }}>
            <T>distributor</T>
          </Typography>.
        </>
      )
    default:
      return null
  }
}

const ActivityFeedItem = ({ props, isLastItem, handleClose: _handleClose, isDashboard, isAffiliate }) => {
  const { currentLanguage, localizeNumber } = useContext(LanguageContext);
  const isSeen = props.isSeen === "true";
  const isClickable = (props?.orderDetails ||
    nonOrderEvents.includes(props.hydraEventType)) &&
    props.hydraEventType !== "new-auto-order"

  const location = useLocation();
  const navigate = useNavigate();
  const { logEvent } = useMixPanel();
  const { sendRequest } = useApi();

  if (!toDisplayEvents.includes(props.hydraEventType)) {
    return null
  }

  const handleItemClick = async () => {
    logEvent('recent_activity_feed_item_click')
    if (!isClickable) {
      logEvent('recent_activity_feed_item_not_clickable', {
        activityId: props.activityId,
        distributorId: props.distributorId,
        distributorName: `${props.firstName} ${props.lastName}`,
        hydraEventType: props.hydraEventType,
        memberId: props.memberId,
      })
      return;
    }

    // Determine the correct drawer using Hydra customer type
    let drawer = "member";
    try {
      const res = await sendRequest({
        method: "get",
        endpoint: `customers?unicity=${props.memberId}&expand=customer`,
      });
      const hydraType = res?.data?.items?.[0]?.type; // e.g., "Associate" for partners
      drawer = hydraType === "Associate" ? "partner" : "member";
    } catch {
      // fall back to member drawer if lookup fails
    }

    const orderHref = props?.orderDetails?.href?.split("/").slice(-1)[0]
    const navigateTo = orderHref
      ? `${location.pathname}?drawer=${drawer}&id=${props.memberId}&drawerContent=orderDetails&orderHref=${orderHref}`
      : `${location.pathname}?drawer=${drawer}&id=${props.memberId}&drawerContent=profile`;
    navigate(navigateTo, { state: location });
  }

  return (
    <Box
      className="activity-feed-item"
      sx={{
        cursor: isClickable ? "pointer" : "default"
      }}
      onClick={handleItemClick}
      data-test-id="activity-feed-item"
    >
      <Stack direction="row" pl={1} pr={2.5}>
        {
          isDashboard || isSeen ?
            <Box width={"13px"}></Box> :
            <Stack justifyContent="center" mr={1}>
              <img src="icons/new-activity.svg" alt="new activity indicator" style={{ height: "5px", width: "5px" }} />
            </Stack>

        }
        <Stack justifyContent="center" mr={1.5}>
          <Avatar 
            id="activity-feed-item-avatar"
            alt={props.firstName}
            src={props.avatar}
            sx={{ width: 24, height: 24 }}
          />
        </Stack>
        <Stack>
          <Stack>
            <Typography variant="widgetItemName">
              <RenderName
                firstName={props.firstName}
                lastName={props.lastName}
                isClickable={isClickable}
              />
              <RenderEventDetails
                localizeNumber={localizeNumber}
                isClickable={isClickable}
                isAffiliate={isAffiliate}
                currentLanguage={currentLanguage}
                {...props}
              />
            </Typography>
          </Stack>
          <Stack>
            <Typography
              variant="widgetItemIimestamp"
              data-test-id={`activity-feed-item-date`}
            >
              {formatDateByLocale(currentLanguage, props.dateCreated, DateTime.DATETIME_MED)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {!isLastItem && <Divider sx={{ marginTop: "16px" }} />}
    </Box>

  )
}

export { ActivityFeedItem }